import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { formatBRL } from '../../../shared/lib/format';
import type {
  Categoria,
  GrupoComplementos,
  Produto,
  ProdutoInput,
  ProdutoTipo,
  TamanhoInput,
} from '../types';

interface Props {
  open: boolean;
  produto: Produto | null;
  categorias: Categoria[];
  grupos: GrupoComplementos[];
  onClose: () => void;
  onSubmit: (input: ProdutoInput) => Promise<void> | void;
  loading?: boolean;
}

interface TamanhoRow extends TamanhoInput {
  _key: string;
}

const key = () => Math.random().toString(36).slice(2);

export function ProdutoFormModal({
  open,
  produto,
  categorias,
  grupos,
  onClose,
  onSubmit,
  loading,
}: Props) {
  const [categoriaId, setCategoriaId] = useState('');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tipo, setTipo] = useState<ProdutoTipo>('unitario');
  const [precoUnitario, setPrecoUnitario] = useState<string>('');
  const [precoPorKg, setPrecoPorKg] = useState<string>('');
  const [codigoBarras, setCodigoBarras] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [tamanhos, setTamanhos] = useState<TamanhoRow[]>([]);
  const [grupoIds, setGrupoIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setCategoriaId(produto?.categoria_id ?? categorias[0]?.id ?? '');
    setNome(produto?.nome ?? '');
    setDescricao(produto?.descricao ?? '');
    setTipo(produto?.tipo ?? 'unitario');
    setPrecoUnitario(produto?.preco_unitario != null ? String(produto.preco_unitario) : '');
    setPrecoPorKg(produto?.preco_por_kg != null ? String(produto.preco_por_kg) : '');
    setCodigoBarras(produto?.codigo_barras ?? '');
    setAtivo(produto?.ativo ?? true);
    setTamanhos(
      (produto?.tamanhos ?? []).map((t) => ({
        _key: key(),
        id: t.id,
        nome: t.nome,
        preco: t.preco,
        ordem: t.ordem,
        ativo: t.ativo,
      })),
    );
    setGrupoIds((produto?.grupos ?? []).map((g) => g.id));
  }, [open, produto, categorias]);

  const addTamanho = () => {
    setTamanhos((p) => [
      ...p,
      { _key: key(), nome: '', preco: 0, ordem: p.length, ativo: true },
    ]);
  };

  const updateTamanho = (k: string, patch: Partial<TamanhoRow>) => {
    setTamanhos((p) => p.map((t) => (t._key === k ? { ...t, ...patch } : t)));
  };

  const removeTamanho = (k: string) => {
    setTamanhos((p) => p.filter((t) => t._key !== k));
  };

  const toggleGrupo = (id: string) => {
    setGrupoIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: ProdutoInput = {
      categoria_id: categoriaId,
      nome: nome.trim(),
      descricao: descricao.trim() || null,
      tipo,
      preco_unitario:
        tipo === 'unitario' && precoUnitario !== '' ? Number(precoUnitario) : null,
      preco_por_kg: tipo === 'peso' && precoPorKg !== '' ? Number(precoPorKg) : null,
      codigo_barras: codigoBarras.trim() || null,
      ativo,
      tamanhos: tipo === 'unitario'
        ? tamanhos
            .filter((t) => t.nome.trim())
            .map((t, idx) => ({
              id: t.id,
              nome: t.nome.trim(),
              preco: Number(t.preco) || 0,
              ordem: idx,
              ativo: t.ativo ?? true,
            }))
        : [],
      grupos_complementos_ids: grupoIds,
    };
    await onSubmit(payload);
  };

  const canSubmit =
    !!categoriaId &&
    nome.trim().length > 0 &&
    (tipo === 'peso'
      ? Number(precoPorKg) > 0
      : tamanhos.some((t) => t.nome.trim()) || Number(precoUnitario) > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={produto ? 'Editar produto' : 'Novo produto'}
      size="xl"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button
            type="submit"
            form="produto-form"
            className="btn-primary"
            disabled={loading || !canSubmit}
          >
            {loading ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <form id="produto-form" onSubmit={handleSubmit} className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Nome</label>
            <input
              type="text"
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={120}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Categoria</label>
            <select
              className="input"
              value={categoriaId}
              onChange={(e) => setCategoriaId(e.target.value)}
              required
            >
              <option value="" disabled>
                Selecione…
              </option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Descricao</label>
          <textarea
            className="input min-h-[72px]"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            maxLength={500}
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">Tipo de venda</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTipo('unitario')}
              className={`rounded-lg border p-3 text-left transition ${
                tipo === 'unitario'
                  ? 'border-brand-600 bg-brand-50 text-brand-900'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="font-medium">Unitario</div>
              <div className="text-xs opacity-80">Preco fixo ou por tamanho</div>
            </button>
            <button
              type="button"
              onClick={() => setTipo('peso')}
              className={`rounded-lg border p-3 text-left transition ${
                tipo === 'peso'
                  ? 'border-brand-600 bg-brand-50 text-brand-900'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="font-medium">Por peso</div>
              <div className="text-xs opacity-80">Self-service por kg</div>
            </button>
          </div>
        </div>

        {tipo === 'peso' ? (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Preco por kg (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              className="input"
              value={precoPorKg}
              onChange={(e) => setPrecoPorKg(e.target.value)}
              required
            />
          </div>
        ) : (
          <>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Preco unitario (opcional - use se nao houver tamanhos)
              </label>
              <input
                type="number"
                step="0.01"
                min={0}
                className="input"
                value={precoUnitario}
                onChange={(e) => setPrecoUnitario(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                  Tamanhos / variacoes
                </label>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm text-brand-700 hover:text-brand-800"
                  onClick={addTamanho}
                >
                  <Plus className="h-4 w-4" /> Adicionar
                </button>
              </div>
              {tamanhos.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-300 py-4 text-center text-xs text-slate-500">
                  Sem tamanhos. Use preco unitario acima ou adicione ao menos um tamanho.
                </p>
              ) : (
                <div className="space-y-2">
                  {tamanhos.map((t) => (
                    <div
                      key={t._key}
                      className="grid grid-cols-12 gap-2 rounded-lg border border-slate-200 p-2"
                    >
                      <input
                        className="input col-span-7"
                        placeholder="Ex: 300ml"
                        value={t.nome}
                        onChange={(e) => updateTamanho(t._key, { nome: e.target.value })}
                      />
                      <div className="col-span-4 flex items-center rounded-lg border border-slate-300 bg-white px-2">
                        <span className="text-xs text-slate-500">R$</span>
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          className="w-full bg-transparent py-2 pl-1 text-sm outline-none"
                          value={t.preco}
                          onChange={(e) =>
                            updateTamanho(t._key, { preco: Number(e.target.value) })
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className="col-span-1 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
                        onClick={() => removeTamanho(t._key)}
                        aria-label="Remover"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">
            Codigo de barras (opcional)
          </label>
          <input
            type="text"
            className="input"
            value={codigoBarras}
            onChange={(e) => setCodigoBarras(e.target.value.replace(/\D/gu, ''))}
            maxLength={14}
            placeholder="7891000000000"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Grupos de complementos
          </label>
          {grupos.length === 0 ? (
            <p className="text-xs text-slate-500">
              Crie grupos na aba correspondente antes de vincular aqui.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {grupos.map((g) => {
                const checked = grupoIds.includes(g.id);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => toggleGrupo(g.id)}
                    className={`rounded-full border px-3 py-1 text-xs transition ${
                      checked
                        ? 'border-brand-600 bg-brand-600 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {g.nome}
                    {g.obrigatorio && <span className="ml-1 opacity-70">*</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={ativo}
            onChange={(e) => setAtivo(e.target.checked)}
          />
          Produto ativo (visivel no PDV)
        </label>

        {tipo === 'unitario' && precoUnitario === '' && tamanhos.length > 0 && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Com tamanhos, o preco exibido no PDV sera{' '}
            <strong>
              {tamanhos[0]?.preco ? `a partir de ${formatBRL(Number(tamanhos[0].preco))}` : '—'}
            </strong>
            .
          </p>
        )}
      </form>
    </Modal>
  );
}
